import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { reduxForm, Field, formValues } from 'redux-form'
import { cx } from '@emotion/css'

import { SingleSearch, Button, ButtonLoader, Checkbox, FormErrorNotification, FormLabel, requiredField, ButtonLink } from '@offerzen/pinata'

import { MarkdownEditor } from '@offerzen/pinata/markdowns'
import { MonthYearPicker } from '@offerzen/pinata/datepickers'

import styles from './styles'

const validators = {
  requiredSearch: value => {
    if (!value.label) return 'Required'

    return undefined
  },
  required: requiredField('Required'),
}

const WorkHistoryItemForm = ({
  isNew,
  touch,
  change,
  handleSubmit,
  error,
  form,
  searchingCompanies,
  searchCompanies,
  companiesSearchResults,
  searchingRoles,
  searchRoles,
  rolesSearchResults,
  start,
  end,
  stillThere,
  submitting,
  toggleEditing,
  deleteBlankWorkHistory,
}) => {
  useEffect(() => {
    // Trigger validations if there is any missing data.
    // ReduxForm doesn't have a better way to do this.
    // `touch()` should be a dependency of the effect, but it's value is not stable due to ReduxForm.
    if (!isNew) touch('company', 'position', 'start', 'end', 'description')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew])

  const startField = useRef(null)
  const endField = useRef(null)

  return (
    <form onSubmit={handleSubmit} className={styles.form} data-test="work-experience-form">
      <If condition={error}>
        <FormErrorNotification text={error} />
      </If>

      <div className={styles.fieldSet}>
        <div>
          <FormLabel htmlFor={`${form}-company`} label="Company name" />
          <Field
            id={`${form}-company`}
            name="company"
            className={cx(styles.fieldInput, styles.searchInput)}
            theme="primary"
            placeholder="Enter a company name"
            fetching={searchingCompanies}
            onSearch={searchCompanies}
            results={companiesSearchResults}
            validate={validators.requiredSearch}
            component={SingleSearch}
            autoFocus
          />
        </div>

        <div>
          <FormLabel htmlFor={`${form}-position`} label="Role name" />
          <Field
            id={`${form}-position`}
            name="position"
            className={cx(styles.fieldInput, styles.searchInput)}
            theme="primary"
            placeholder="Enter a role name"
            fetching={searchingRoles}
            onSearch={searchRoles}
            results={rolesSearchResults}
            validate={validators.requiredSearch}
            component={SingleSearch}
          />
        </div>

        <div>
          <FormLabel htmlFor={`${form}-start`} label="Start date" />
          <Field
            name="start"
            innerRef={el => {
              startField.current = el
            }}
            placeholder="Select start date"
            maxDate={end || new Date()}
            theme="primary"
            validate={validators.required}
            component={MonthYearPicker}
            onBlur={ev => {
              // HACK: bandaid fix for a bug where MonthYearPicker changes the Date format to a format incompatible with Firefox
              ev.preventDefault()
            }}
            className={styles.fieldInput}
            id={`${form}-start`}
          />
        </div>

        <div>
          <FormLabel htmlFor={`${form}-end`} label="End date" />
          <Field
            name="end"
            innerRef={el => {
              endField.current = el
            }}
            placeholder="Select end date"
            minDate={start}
            maxDate={new Date()}
            theme="primary"
            readOnly={stillThere}
            component={MonthYearPicker}
            onBlur={ev => {
              // HACK: bandaid fix for a bug where MonthYearPicker changes the Date format to a format incompatible with Firefox
              ev.preventDefault()
            }}
            className={styles.fieldInput}
            validate={!stillThere && validators.required}
            id={`${form}-end`}
          />
        </div>
      </div>

      <Field
        theme="primary"
        name="still_there"
        id={`${form}.still_there`}
        label="I currently work here"
        onChange={value => value && change('end', null)}
        component={Checkbox}
        className={styles.currentWorkField}
        checked={false}
      />

      <div className={styles.fieldset}>
        <FormLabel htmlFor={`${form}-description`} label="Description" optional />

        <Field
          name="description"
          placeholder="Write a description of your role"
          theme="primary"
          size="small"
          component={MarkdownEditor}
          className={styles.description}
          id={`${form}-description`}
        />
      </div>

      <div className={styles.buttons}>
        <ButtonLink
          className={styles.cancel}
          size="small"
          label="Cancel"
          theme="light"
          onClick={() => {
            if (isNew) deleteBlankWorkHistory()
            else toggleEditing(false)
          }}
        />
        <Choose>
          <When condition={submitting}>
            <ButtonLoader size="medium" theme="primary" variant="outlined" label="Saving" />
          </When>

          <Otherwise>
            <Button label="Save" size="medium" theme="primary" variant="outlined" type="submit" />
          </Otherwise>
        </Choose>
      </div>
    </form>
  )
}

WorkHistoryItemForm.propTypes = {
  form: PropTypes.string.isRequired,
  isNew: PropTypes.bool,
  touch: PropTypes.func,
  change: PropTypes.func,
  handleSubmit: PropTypes.func,
  error: PropTypes.string,
  searchingCompanies: PropTypes.bool,
  searchCompanies: PropTypes.func,
  companiesSearchResults: PropTypes.arrayOf(
    PropTypes.objectOf({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  searchingRoles: PropTypes.bool,
  searchRoles: PropTypes.func,
  rolesSearchResults: PropTypes.arrayOf(
    PropTypes.objectOf({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  start: PropTypes.instanceOf(Date),
  end: PropTypes.instanceOf(Date),
  stillThere: PropTypes.bool,
  submitting: PropTypes.bool,
  toggleEditing: PropTypes.func,
  deleteBlankWorkHistory: PropTypes.func,
}

export default reduxForm({
  onSubmit: (values, _, props) =>
    new Promise((resolve, reject) => {
      const data = {
        ...values,
        company: values.company.label,
        position: values.position.label,
      }

      if (props.isNew) props.createWorkHistory(data, resolve, reject)
      else props.updateWorkHistory(data, resolve, reject)
    }),
  onSubmitSuccess: (_, __, props) => {
    if (props.isNew) props.deleteBlankWorkHistory()
    else props.toggleEditing(false)
  },
  touchOnBlur: false,
})(
  formValues({
    stillThere: 'still_there',
    start: 'start',
    end: 'end',
  })(WorkHistoryItemForm),
)
